import { useState, useEffect } from 'react'
import { salespersonAPI } from '../../services/api'
import SalespersonDetailPage from './SalespersonDetailPage'

const PAGE_SIZE = 10

function SalespersonPage() {
  const [detailId, setDetailId] = useState(null)

  // 상세 화면 모드
  if (detailId) {
    return <SalespersonDetailPage salespersonId={detailId} onBack={() => setDetailId(null)} />
  }

  return <SalespersonListView onViewDetail={(id) => setDetailId(id)} />
}

function SalespersonListView({ onViewDetail }) {
  const [salespersons, setSalespersons] = useState([])
  const [stats, setStats] = useState({ totalSalespersons: 0, totalReferrals: 0, totalSettlement: 0, monthlyReferrals: 0 })
  const [selectedId, setSelectedId] = useState(null)
  const [referrals, setReferrals] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newSalesperson, setNewSalesperson] = useState({ name: '', loginId: '', phone: '', email: '', memo: '', password: '' })
  const [creating, setCreating] = useState(false)
  const [loginIdChecked, setLoginIdChecked] = useState(false) // 중복 체크 완료 여부
  const [loginIdMessage, setLoginIdMessage] = useState('') // 중복 체크 결과 메시지
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (selectedId) {
      loadReferrals(selectedId)
    }
  }, [selectedId])

  // 검색 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listRes, statsRes] = await Promise.all([
        salespersonAPI.getAll(),
        salespersonAPI.getStats(),
      ])
      setSalespersons(listRes.data || [])
      setStats(statsRes.data || {})
    } catch (err) {
      console.error('영업사원 데이터 로드 실패:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadReferrals = async (id) => {
    try {
      const res = await salespersonAPI.getReferrals(id)
      setReferrals(res.data || [])
    } catch (err) {
      console.error('추천 유저 로드 실패:', err)
    }
  }

  const handleCheckLoginId = async () => {
    if (!newSalesperson.loginId.trim()) return
    try {
      const res = await salespersonAPI.checkLoginId(newSalesperson.loginId)
      if (res.data.exists) {
        setLoginIdChecked(false)
        setLoginIdMessage('이미 사용 중인 아이디입니다.')
      } else {
        setLoginIdChecked(true)
        setLoginIdMessage('사용 가능한 아이디입니다.')
      }
    } catch (err) {
      setLoginIdChecked(false)
      setLoginIdMessage('중복 확인에 실패했습니다.')
    }
  }

  const handleCreate = async () => {
    if (!newSalesperson.name || !newSalesperson.loginId || !newSalesperson.password || !loginIdChecked) return
    try {
      setCreating(true)
      await salespersonAPI.create(newSalesperson)
      setShowCreateModal(false)
      setNewSalesperson({ name: '', loginId: '', phone: '', email: '', memo: '', password: '' })
      setLoginIdChecked(false)
      setLoginIdMessage('')
      await loadData()
    } catch (err) {
      alert(err.message || '등록 실패')
    } finally {
      setCreating(false)
    }
  }

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    try {
      await salespersonAPI.updateStatus(id, newStatus)
      await loadData()
    } catch (err) {
      console.error('상태 변경 실패:', err)
    }
  }

  // 유저명 마스킹: "홍길동" → "홍**"
  const maskName = (name) => {
    if (!name || name.length <= 1) return name
    return name.charAt(0) + '**'
  }

  // CSV 내보내기
  const handleExportCSV = () => {
    if (referrals.length === 0) return
    const header = '유저명,유저ID,가입일,구독 플랜,적용 혜택,최종 결제액\n'
    const rows = referrals.map(ref =>
      `${maskName(ref.userName)},${ref.userId},${ref.createdAt ? new Date(ref.createdAt).toLocaleDateString('ko-KR') : '-'},${ref.plan},${ref.benefit || '없음'},${ref.paymentAmount}`
    ).join('\n')

    const bom = '\uFEFF'
    const blob = new Blob([bom + header + rows], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedSalesperson?.name}_referrals.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const selectedSalesperson = salespersons.find(s => s.id === selectedId)

  const filteredSalespersons = salespersons.filter(s =>
    s.name.includes(searchQuery) || s.referralCode.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 페이지네이션
  const totalPages = Math.max(1, Math.ceil(filteredSalespersons.length / PAGE_SIZE))
  const paginatedSalespersons = filteredSalespersons.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <header className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">영업사원 관리</h2>
        <div className="flex items-center gap-3">
          <div className="relative flex items-center group">
            <span className="material-symbols-outlined absolute left-3 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
            <input
              className="w-64 pl-10 pr-4 py-2 text-sm bg-slate-100 dark:bg-slate-900 border-none rounded-lg focus:ring-2 focus:ring-primary transition-all"
              placeholder="영업사원 이름 또는 코드 검색"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg relative">
            <span className="material-symbols-outlined">notifications</span>
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-background-dark"></span>
          </button>
        </div>
      </header>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col gap-1">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">전체 영업사원</span>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">{stats.totalSalespersons}</span>
            <span className="text-sm text-green-500 mb-1 flex items-center">
              <span className="material-symbols-outlined text-sm">trending_up</span> {stats.totalSalespersons}명
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col gap-1">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">이번 달 가입 유저</span>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">{(stats.monthlyReferrals || stats.totalReferrals)?.toLocaleString()}</span>
            <span className="text-sm text-green-500 mb-1 flex items-center">
              <span className="material-symbols-outlined text-sm">trending_up</span> 12.5%
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col gap-1">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">총 정산 예정 금액</span>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold">₩{stats.totalSettlement?.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* 영업사원 목록 테이블 */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
        <div className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h3 className="text-lg font-bold">영업사원 목록</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">활동 중인 모든 영업사원의 코드를 관리합니다.</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-all shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-[20px]">add_circle</span>
            새 영업사원 코드 발급
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">이름</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">추천 코드</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">가입 유저</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">총 정산액</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">상태</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {paginatedSalespersons.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-slate-500">
                    등록된 영업사원이 없습니다
                  </td>
                </tr>
              ) : (
                paginatedSalespersons.map(sp => (
                  <tr
                    key={sp.id}
                    className={`transition-colors cursor-pointer ${
                      selectedId === sp.id
                        ? 'bg-primary/5 border-l-4 border-primary dark:bg-primary/10'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                    onClick={() => setSelectedId(selectedId === sp.id ? null : sp.id)}
                  >
                    <td className="px-6 py-4 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                        selectedId === sp.id
                          ? 'bg-primary text-white'
                          : sp.status === 'ACTIVE'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                      }`}>
                        {sp.name?.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold">
                        {sp.name}{selectedId === sp.id ? ' (선택됨)' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded font-mono text-xs text-primary font-bold">
                        {sp.referralCode}
                      </code>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">{sp.referralCount}명</td>
                    <td className="px-6 py-4 text-sm font-medium">₩{sp.totalSettlement?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-[10px] font-bold rounded-full border ${
                        sp.status === 'ACTIVE'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-900/50'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'
                      }`}>
                        {sp.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewDetail(sp.id) }}
                        className="text-slate-400 hover:text-primary transition-colors"
                        title="상세 보기"
                      >
                        <span className="material-symbols-outlined">visibility</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStatusToggle(sp.id, sp.status) }}
                        className="text-slate-400 hover:text-primary transition-colors"
                        title="상태 변경"
                      >
                        <span className="material-symbols-outlined">more_vert</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* 페이지네이션 */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <span className="text-xs text-slate-500">
            Showing {paginatedSalespersons.length} of {filteredSalespersons.length} agents
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors disabled:opacity-30"
            >
              <span className="material-symbols-outlined text-sm">chevron_left</span>
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (currentPage <= 3) {
                pageNum = i + 1
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = currentPage - 2 + i
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-xs font-medium rounded ${
                    currentPage === pageNum
                      ? 'bg-primary text-white font-bold'
                      : 'hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors disabled:opacity-30"
            >
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {/* 가입 유저 상세 (선택된 영업사원이 있을 때) */}
      {selectedSalesperson && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">
                가입 유저 상세
                <span className="text-primary text-sm font-normal ml-2">
                  {selectedSalesperson.name}({selectedSalesperson.referralCode})
                </span>
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">해당 영업사원 코드를 통해 가입한 유저 목록입니다.</p>
            </div>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">download</span>
              CSV 내보내기
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800">
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">유저명</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">가입일</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">구독 플랜</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">적용 혜택</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">최종 결제액</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {referrals.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-sm text-slate-500">
                      가입된 유저가 없습니다
                    </td>
                  </tr>
                ) : (
                  referrals.map(ref => (
                    <tr key={ref.id}>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium">{maskName(ref.userName)} ({ref.userId})</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                        {ref.createdAt ? new Date(ref.createdAt).toLocaleDateString('ko-KR') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-[10px] font-bold rounded border ${
                          ref.plan === 'PREMIUM'
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : ref.plan === 'ENTERPRISE'
                              ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                        }`}>
                          {ref.plan}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {ref.benefit ? (
                          <span className="text-xs font-medium text-red-500">{ref.benefit}</span>
                        ) : (
                          <span className="text-xs font-medium text-slate-400">없음</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold">₩{ref.paymentAmount?.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 새 영업사원 등록 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 w-full max-w-md border border-slate-200 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">새 영업사원 코드 발급</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">이름 *</label>
                <input
                  className="w-full px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                  value={newSalesperson.name}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, name: e.target.value })}
                  placeholder="영업사원 이름"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">로그인 아이디 *</label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary font-mono"
                    value={newSalesperson.loginId}
                    onChange={(e) => {
                      setNewSalesperson({ ...newSalesperson, loginId: e.target.value })
                      setLoginIdChecked(false)
                      setLoginIdMessage('')
                    }}
                    placeholder="영업사원 로그인 아이디"
                  />
                  <button
                    type="button"
                    onClick={handleCheckLoginId}
                    disabled={!newSalesperson.loginId.trim()}
                    className="px-3 py-2 text-xs font-bold text-white bg-slate-600 hover:bg-slate-700 rounded-lg disabled:opacity-50 whitespace-nowrap"
                  >
                    중복 확인
                  </button>
                </div>
                {loginIdMessage && (
                  <p className={`text-xs mt-1 font-medium ${loginIdChecked ? 'text-green-500' : 'text-red-500'}`}>
                    {loginIdMessage}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">연락처</label>
                <input
                  className="w-full px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                  value={newSalesperson.phone}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, phone: e.target.value })}
                  placeholder="010-0000-0000"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">이메일</label>
                <input
                  className="w-full px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                  value={newSalesperson.email}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, email: e.target.value })}
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">메모</label>
                <input
                  className="w-full px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                  value={newSalesperson.memo}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, memo: e.target.value })}
                  placeholder="비고"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500 mb-1 block">비밀번호 *</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                  value={newSalesperson.password}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, password: e.target.value })}
                  placeholder="로그인용 비밀번호"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 text-sm font-medium text-slate-500 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newSalesperson.name || !newSalesperson.loginId || !newSalesperson.password || !loginIdChecked}
                className="flex-1 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {creating ? '등록 중...' : '발급하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalespersonPage
